import { useUser } from "@dashboard/auth";
import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import { DashboardCard } from "@dashboard/components/Card";
import { ListPageLayout } from "@dashboard/components/Layouts";
import {
  LabelBatchNumbersQuery,
  useLabelBatchNumbersQuery,
  useVariantBatchNumberUpdateMutation,
} from "@dashboard/graphql";
import useDebounce from "@dashboard/hooks/useDebounce";
import { Box, Button, Input, Text } from "@saleor/macaw-ui-next";
import { ChevronRight } from "lucide-react";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { BatchFormDialog, BatchFormValues, toDateTimeLocalValue } from "../../BatchFormDialog";
import { batchMessages } from "../../batchMessages";
import { messages as overviewMessages } from "../LabelPrintingOverview/messages";

type BatchRow = NonNullable<LabelBatchNumbersQuery["labelBatchNumbers"]>[number];

const formatArrival = (iso: string, locale: string) =>
  new Date(iso).toLocaleString(locale, { dateStyle: "medium", timeStyle: "short" });

// Admin control of every registered batch and what it corresponds to
// (product/variant, arrival time, supplier, shipment reference, quantity),
// since the code printed on a label is only useful if it traces back to this.
export const LabelBatches = () => {
  const intl = useIntl();
  const user = useUser();
  const isAdmin = !!user?.user?.isSuperuser;
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const applySearch = useDebounce((value: string) => setSearch(value), 300);
  const { data, loading, refetch } = useLabelBatchNumbersQuery({
    variables: { search: search || undefined, limit: 300 },
    skip: !isAdmin,
    fetchPolicy: "network-only",
  });
  const [updateBatch, updateBatchResult] = useVariantBatchNumberUpdateMutation();
  const [editing, setEditing] = useState<BatchRow | null>(null);
  const [error, setError] = useState<string | undefined>();

  const batches = data?.labelBatchNumbers ?? [];

  const handleSubmit = async (values: BatchFormValues) => {
    if (!editing) {
      return;
    }

    // Only send the arrival when it was changed (the form edits at minute
    // precision, so an untouched value would otherwise drop the seconds).
    const arrivalChanged = values.arrivalAt !== toDateTimeLocalValue(new Date(editing.arrivalAt));
    const result = await updateBatch({
      variables: {
        id: editing.id,
        arrivalAt: arrivalChanged ? new Date(values.arrivalAt).toISOString() : undefined,
        isEstimated: values.isEstimated !== editing.isEstimated ? values.isEstimated : undefined,
        supplier: values.supplier,
        reference: values.reference,
        quantity: values.quantity === "" ? undefined : Number(values.quantity),
        notes: values.notes,
      },
    });

    if (result.data?.variantBatchNumberUpdate?.productErrors.length) {
      setError(intl.formatMessage(batchMessages.genericError));

      return;
    }

    setError(undefined);
    setEditing(null);
    await refetch();
  };

  return (
    <ListPageLayout>
      <TopNav
        title={intl.formatMessage(overviewMessages.pageTitle)}
        isAlignToRight={false}
        withoutBorder
      >
        <Box __flex={1} display="flex" alignItems="center">
          <Box marginX={3} display="flex" alignItems="center">
            <ChevronRight size={18} />
          </Box>
          <Text size={6}>
            <FormattedMessage {...batchMessages.pageTitle} />
          </Text>
        </Box>
      </TopNav>
      <Box display="flex" flexDirection="column" gap={4} padding={6}>
        {!isAdmin ? (
          <Text size={3}>
            <FormattedMessage {...batchMessages.adminOnly} />
          </Text>
        ) : (
          <>
            <Text size={2} color="default2">
              <FormattedMessage {...batchMessages.pageDescription} />
            </Text>
            <Box style={{ maxWidth: 420 }}>
              <Input
                size="small"
                label={intl.formatMessage(batchMessages.search)}
                value={searchInput}
                onChange={event => {
                  setSearchInput(event.target.value);
                  applySearch(event.target.value);
                }}
              />
            </Box>
            <DashboardCard>
              <DashboardCard.Content>
                {batches.length === 0 && !loading ? (
                  <Text size={2} color="default2">
                    <FormattedMessage {...batchMessages.empty} />
                  </Text>
                ) : (
                  <Box style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead>
                        <tr style={{ textAlign: "left" }}>
                          {[
                            batchMessages.columnCode,
                            batchMessages.columnProduct,
                            batchMessages.columnArrival,
                            batchMessages.columnSupplier,
                            batchMessages.columnQuantity,
                            batchMessages.columnRegisteredBy,
                          ].map(column => (
                            <th key={column.id} style={{ padding: "8px 12px 8px 0" }}>
                              <Text size={2} fontWeight="bold">
                                {intl.formatMessage(column)}
                              </Text>
                            </th>
                          ))}
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {batches.map(batch => (
                          <tr
                            key={batch.id}
                            style={{
                              borderTop: "1px solid var(--mu-colors-border-default1, #e0e3e8)",
                            }}
                          >
                            <td style={{ padding: "8px 12px 8px 0" }}>
                              <Text size={2} fontWeight="bold">
                                {batch.code}
                              </Text>
                            </td>
                            <td style={{ padding: "8px 12px 8px 0" }}>
                              <Text size={2}>{batch.productName}</Text>
                              <br />
                              <Text size={1} color="default2">
                                {[batch.variantName, batch.sku].filter(Boolean).join(" — ")}
                              </Text>
                            </td>
                            <td style={{ padding: "8px 12px 8px 0" }}>
                              <Text size={2}>{formatArrival(batch.arrivalAt, intl.locale)}</Text>
                              {batch.isEstimated && (
                                <>
                                  <br />
                                  <Text size={1} color="warning1">
                                    {intl.formatMessage(batchMessages.etaBadge)}
                                  </Text>
                                </>
                              )}
                            </td>
                            <td style={{ padding: "8px 12px 8px 0" }}>
                              <Text size={2}>{batch.supplier || "—"}</Text>
                              <br />
                              <Text size={1} color="default2">
                                {batch.reference || ""}
                              </Text>
                              {batch.notes && (
                                <>
                                  <br />
                                  <Text size={1} color="default2">
                                    {batch.notes}
                                  </Text>
                                </>
                              )}
                            </td>
                            <td style={{ padding: "8px 12px 8px 0" }}>
                              <Text size={2}>{batch.quantity ?? "—"}</Text>
                            </td>
                            <td style={{ padding: "8px 12px 8px 0" }}>
                              <Text size={2}>{batch.createdByName || "—"}</Text>
                            </td>
                            <td style={{ padding: "8px 0", textAlign: "right" }}>
                              <Button
                                variant="secondary"
                                size="small"
                                onClick={() => {
                                  setError(undefined);
                                  setEditing(batch);
                                }}
                              >
                                {intl.formatMessage(batchMessages.edit)}
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Box>
                )}
              </DashboardCard.Content>
            </DashboardCard>
          </>
        )}
      </Box>
      {editing && (
        <BatchFormDialog
          open
          mode="edit"
          subtitle={`${editing.code} — ${editing.productName} ${editing.sku ?? ""}`}
          loading={updateBatchResult.loading}
          error={error}
          initialValues={{
            arrivalAt: toDateTimeLocalValue(new Date(editing.arrivalAt)),
            isEstimated: editing.isEstimated,
            supplier: editing.supplier ?? "",
            reference: editing.reference ?? "",
            quantity: editing.quantity?.toString() ?? "",
            notes: editing.notes ?? "",
          }}
          onClose={() => setEditing(null)}
          onSubmit={handleSubmit}
        />
      )}
    </ListPageLayout>
  );
};
